export interface FeatureFlagMeta {
  backend: string[]
  frontend: string[]
  whenEnabled: string[]
  whenDisabled: string[]
  rollout: string[]
  defaultState: string
  notes?: string[]
}

export const FEATURE_FLAG_META: Record<string, FeatureFlagMeta> = {
  learning_intelligence: {
    backend: ['LearnerInsightService', 'LearningSessionService.getPlan()'],
    frontend: ['CoachNoteCard on Continue Learning (/learn)'],
    whenEnabled: [
      'LearnerInsightService analyses review failures, synonym links, neglected words, and form gaps.',
      'Eligible users see a coach note card on Continue Learning with a personalised message and CTA (practice, challenge, or open word).',
      'Insight types: confusion cluster, synonym mix-up, neglected word (21+ days idle), form gap (seen inflected form but not base forms).',
      'Dismissed insights are suppressed for 7 days for the same word cluster.',
    ],
    whenDisabled: [
      'findActiveInsight() returns empty — no new insights are detected or shown.',
      'CoachNoteCard is not rendered; the learning plan works as before without coach guidance.',
      'Existing insight rows in the database are kept but never surfaced.',
    ],
    rollout: [
      'Master switch OFF → feature is off for everyone, including admins.',
      'Master switch ON + rollout N% → only users whose stable hash bucket (userId + flag key) falls below N% see coach notes.',
      'Admins always receive the feature when the master switch is ON, regardless of rollout %.',
    ],
    defaultState: 'Enabled, 100% rollout (production default since Phase 29).',
    notes: [
      'Does not consume AI credits — purely rule-based detection from review logs and reading stats.',
    ],
  },

  content_scoring: {
    backend: [
      'ArticleScoringService (scores computed on article index/generation)',
      'ReadingRecommendationService (ranking + difficulty label)',
      'ArticleService (article list difficulty label)',
    ],
    frontend: ['ArticleCard difficulty badge (Comfortable / Stretch / Challenging)'],
    whenEnabled: [
      'Article scores (vocabulary density, grammar complexity, retention score) influence reading recommendations — adds up to +8 points to ranking via retention score.',
      'Article cards show a personalised difficulty label relative to the user\'s target CEFR level: COMFORTABLE, STRETCH, or CHALLENGING.',
      'Scores are pre-computed in the background when articles are indexed; the flag only controls whether users see and benefit from them.',
    ],
    whenDisabled: [
      'Recommendations use only level match, tags, due-word overlap, and time budget — retention score is ignored.',
      'difficultyLabel is null on all article responses; no difficulty badge on article cards.',
      'ArticleScore rows still exist in the database and continue to be computed — only user-facing usage is gated.',
    ],
    rollout: [
      'Gradual rollout lets you A/B test whether scored recommendations improve engagement before full release.',
      'Admins see scoring effects immediately when the master switch is ON.',
    ],
    defaultState: 'Disabled, 0% rollout (Phase 32 — not yet launched).',
    notes: [
      'explainability can surface grammar complexity in "Why this article?" only when content_scoring data exists; explainability is a separate flag.',
    ],
  },

  explainability: {
    backend: [
      'ReadingRecommendationService.finalizePick()',
      'RecommendationExplanationBuilder',
    ],
    frontend: [
      'RecommendationWhyPanel ("Why this article?") on Reading hub',
      'RecommendedArticleHero',
      'Learning session reading step',
    ],
    whenEnabled: [
      'Recommended articles include a structured explanations[] array with reason codes: LEVEL_MATCH, LEVEL_NEAR, DUE_WORDS, TAG_AFFINITY, TIME_FIT, READING_COMPLEXITY.',
      'Users can expand "Why this article?" to see human-readable reasons and matched due words.',
      'If content_scoring data exists, adds a sentence-complexity explanation (comfortable / balanced / stretch read).',
    ],
    whenDisabled: [
      'explanations[] is always an empty list — RecommendationWhyPanel is hidden.',
      'Basic reason chips (level, tags, overlap, time) in the hero still show via the separate reasons[] field.',
    ],
    rollout: [
      'Use rollout to test whether transparency improves trust and click-through on recommendations.',
      'Admins always see explanations when the master switch is ON.',
    ],
    defaultState: 'Disabled, 0% rollout (Phase 32 — not yet launched).',
    notes: [
      'Independent from content_scoring for ranking, but READING_COMPLEXITY explanations need ArticleScore data.',
    ],
  },

  language_unit_resolver: {
    backend: [
      'LanguageUnitResolver + CatalogMwuMatcher',
      'UnderstandingService.tryLanguageUnitPath()',
      'PhraseExplanationService (catalog SEED before AI)',
    ],
    frontend: [
      'WordPopup phrase-primary layout + nested word section',
      'Span flash (.lexio-span-flash) in ArticleReaderPage and StoryDisplay',
    ],
    whenEnabled: [
      'Single-word tap in reading runs catalog MWU detection on sentence context.',
      'Known phrases (e.g. at least, by chance, after all) open as PHRASE with nested WORD for the tapped token.',
      'Multi-token matches briefly highlight the full phrase span in the article.',
      'Catalog SEED meanings are used without AI credits when available.',
    ],
    whenDisabled: [
      'Legacy understand path only — every tap resolves as WORD (or explicit multi-word selection as PHRASE).',
      'No span flash; no silent MWU override.',
    ],
    rollout: [
      'Master switch OFF → feature off for everyone except per-user overrides.',
      'Master switch ON + rollout N% → stable hash bucket on userId + flag key.',
      'Admins always receive the feature when the master switch is ON.',
    ],
    defaultState: 'Enabled, 100% rollout (dogfood after Slice 0–2, V72).',
    notes: [
      'Only contiguous catalog phrases match — at home is not in seed; at least and by chance are.',
      'Phrase ru/uk localizations may show English SEED meaning until phrase localization pipeline ships.',
    ],
  },

  grammar_pattern_matcher: {
    backend: [
      'GrammarPatternMatcher in LanguageUnitResolver',
      'LemmaResolverGrammarTokenAnnotator (morphology slots)',
      'UnderstandingService (requires language_unit_resolver)',
      'POST /api/understand/grammar-spans (Track C — backend only; FE paused)',
    ],
    frontend: [
      'Same phrase-primary popup as MWU when grammar wins silently',
    ],
    whenEnabled: [
      'Single-word tap can resolve to GRAMMAR_PATTERN (e.g. has seen, should have told) via template matcher.',
      'Band A → silent primary; Band B (PROGRESSIVE, PASSIVE_BE, THERE_BE, negation) → CHOOSER.',
      'Contractions expanded before match (haven\'t → have + not).',
      'Catalog MWU still wins on type rank + span length when both match.',
      'Phrase explanation uses pattern cards (EN/RU/UK) — no AI debit for grammar hits.',
      'Save-to-vocabulary is hidden for GRAMMAR_PATTERN (construction, not a lexical phrase).',
    ],
    whenDisabled: [
      'GrammarPatternMatcher is skipped; only catalog MWU + word fallback run.',
      'language_unit_resolver behavior unchanged.',
      'grammar-spans returns an empty list.',
    ],
    rollout: [
      'Requires language_unit_resolver ON for the same user (tap path).',
      'Master switch OFF → grammar matching off for everyone except per-user overrides.',
      'Admins always receive the feature when the master switch is ON.',
    ],
    defaultState: 'Disabled, 0% rollout (G1/G2 — dogfood via admin flags).',
    notes: [
      'Nested under Intelligence group in admin UI alongside language_unit_resolver.',
      'Cards are keyed by patternId (PERFECT, MODAL_PERFECT, …), never by surface string.',
      'Track C reading highlight UI is paused until scan latency is fixed.',
    ],
  },

  meaning_composition: {
    backend: [
      'UnderstandingService grammar path → MeaningAssembler + MeaningPresenter',
      'LexemeFeatures + SoftGapCueExtractor (M0b–M2)',
      'UnderstandResponse.composedHero dual-write',
    ],
    frontend: [
      'WordPopup grammar body: composedHero as meaning hero when present (M3)',
      'No FE flag check — null composedHero keeps Track A skim',
    ],
    whenEnabled: [
      'Grammar taps may return composedHero (RU/UK Present packs for Band A Perfect family).',
      'Popup shows Meaning hero first; pedagogy title/recipe and nested verb stay secondary.',
    ],
    whenDisabled: [
      'composedHero stays null; popup keeps Track A nested-verb-first skim.',
    ],
    rollout: [
      'Wrong hero = P0 Trust — turn master switch OFF (no FE rollback needed).',
      'Dogfood via admin flag before closed-beta default on.',
    ],
    defaultState: 'Disabled, 0% rollout (M1–M3 shipped; enable for dogfood).',
    notes: [
      'Requires grammar_pattern_matcher + language_unit_resolver for grammar taps.',
      'Underdetermined / opaque spans stay without composedHero (escalate, do not force).',
    ],
  },

  grammar_targeted_practice: {
    backend: [
      'AiPracticeService.submitExercisesJob / submitStoryJob (patternIds)',
      'PromptBuilder lesson scenario + exercises + story',
      'GrammarScenarioCoverageChecker (soft gate + retry)',
    ],
    frontend: [
      'Grammar focus chips paused — patternIds not sent from Practice UI until UX is ready.',
    ],
    whenEnabled: [
      'If patternIds are supplied (admin/API), Lesson and Story may inject Band A constructions.',
      'Generated text is checked with GrammarPatternMatcher; one regenerate if miss.',
      'Lesson exercises prompt asks for items that probe those constructions.',
    ],
    whenDisabled: [
      'patternIds from the client are ignored; practice stays vocabulary-only.',
    ],
    rollout: [
      'Independent of grammar_pattern_matcher (generation vs detection).',
      'Master switch OFF → no grammar injection for anyone except per-user overrides.',
      'Admins receive the feature when the master switch is ON.',
    ],
    defaultState: 'Disabled, 0% rollout (G4a shipped; G4b FE chips paused).',
    notes: [
      'Same flag covers Lesson (G4a) and Story (G4b). FE chips hidden until ready.',
    ],
  },

  premium: {
    backend: ['AiCreditService.resolveTier() → QuotaTier.PRO'],
    frontend: [
      'Settings → AI usage panel (tier badge and limits)',
      'Phrase explanation quota checks',
    ],
    whenEnabled: [
      'User is treated as PRO tier for AI quotas (not ADMIN — admins have unlimited regardless).',
      'Daily AI credits: 300 (vs 15 free).',
      'Daily new phrase AI explanations: 50 (vs 20 free).',
      'Cached word/phrase explanations remain free and unlimited for all tiers.',
    ],
    whenDisabled: [
      'User stays on FREE tier quotas unless they have ADMIN role.',
      'No billing integration yet — this flag is a manual per-user premium grant until subscription (PD-1) ships.',
    ],
    rollout: [
      'Rollout % selects which non-admin users receive PRO quotas — useful for beta testers or gradual premium rollout.',
      'Admins are never upgraded to PRO by this flag; they use ADMIN tier (unlimited).',
      'Consistent hash on userId + flag key — the same user always gets the same result for a given rollout %.',
    ],
    defaultState: 'Disabled, 0% rollout.',
    notes: [
      'Hidden explain-word AI cap (100 calls/day) applies equally to FREE and PRO; only daily credits and phrase explanations differ.',
      'Global AI rate limit (500/min) applies to all tiers.',
    ],
  },
}
